document.addEventListener('DOMContentLoaded', () => {
    const nav = document.getElementById('primary-navigation');
    const toggle = document.getElementById('menu-toggle');

    if (!nav) return;

    const OPEN = 'show';
    const ITEM = '.menu-item-has-children';

    // ─── Accessible nav label ────────────────────────────────────────────────
    if (!nav.getAttribute('aria-label') && !nav.getAttribute('aria-labelledby')) {
        nav.setAttribute('aria-label', 'Primary navigation');
    }

    // ─── Mobile nav toggle ───────────────────────────────────────────────────
    // Initialise ARIA state before any interaction
    toggle?.setAttribute('aria-expanded', 'false');
    // Only hide nav on mobile (when the toggle button is actually visible).
    // aria-hidden alone doesn't remove elements from tab order — inert handles both.
    if (toggle && getComputedStyle(toggle).display !== 'none') {
        nav.setAttribute('aria-hidden', 'true');
        nav.inert = true;
    }

    function isMobileNavOpen() {
        return toggle?.classList.contains(OPEN) ?? false;
    }

    function openMobileNav() {
        toggle.classList.add(OPEN);
        toggle.setAttribute('aria-expanded', 'true');
        nav.classList.add(OPEN);
        nav.removeAttribute('aria-hidden');
        nav.inert = false;
        document.body.classList.add('menu-open');
    }

    function closeMobileNav() {
        toggle.classList.remove(OPEN);
        toggle.setAttribute('aria-expanded', 'false');
        nav.classList.remove(OPEN);
        nav.setAttribute('aria-hidden', 'true');
        nav.inert = true;
        document.body.classList.remove('menu-open');
    }

    toggle?.addEventListener('click', (e) => {
        e.preventDefault();
        isMobileNavOpen() ? closeMobileNav() : openMobileNav();
    });

    // ─── Submenu toggles ─────────────────────────────────────────────────────
    nav.querySelectorAll(ITEM).forEach((item, i) => {
        const submenu = item.querySelector(':scope > .sub-menu');
        const link = item.querySelector(':scope > a');

        if (!submenu || !link) return;

        // Avoid double-init if script runs more than once
        if (item.querySelector(':scope > .sub-toggle')) return;

        // Ensure submenu has a stable ID for aria-controls
        if (!submenu.id) {
            const base = (item.id || `menu-item-${i}`).replace(/[^a-zA-Z0-9_-]/g, '');
            submenu.id = `submenu-${base}`;
        }

        // Initialize closed state
        submenu.classList.remove(OPEN);
        submenu.setAttribute('aria-hidden', 'true');
        submenu.inert = true;

        // Create the toggle button — the <a> remains a real navigable link on
        // desktop. On mobile, clicking the link also triggers the toggle so the
        // wider tap target opens the submenu panel.
        const labelText = link.textContent.trim() || 'submenu';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sub-toggle';
        btn.setAttribute('aria-expanded', 'false');
        btn.setAttribute('aria-controls', submenu.id);
        btn.setAttribute('aria-label', `Toggle ${labelText}`);
        link.after(btn);

        // Back button (hidden on desktop via CSS, visible in mobile panel)
        if (!submenu.querySelector('.sub-menu-back')) {
            const backItem = document.createElement('li');
            backItem.className = 'sub-menu-back';
            const backBtn = document.createElement('button');
            backBtn.type = 'button';
            backBtn.className = 'sub-back-btn';
            backBtn.setAttribute('aria-label', `Back to ${labelText}`);
            backBtn.textContent = labelText;
            backItem.append(backBtn);
            submenu.prepend(backItem);

            backBtn.addEventListener('click', () => {
                setState(btn, submenu, false);
                btn.focus();
            });
        }

        // Clicking the parent link also opens the submenu (wider tap target on
        // mobile). The link remains keyboard-navigable via Enter.
        link.addEventListener('click', (e) => {
            e.preventDefault();
            btn.click();
        });

        btn.addEventListener('click', () => {
            const isOpen = btn.getAttribute('aria-expanded') === 'true';
            closeSiblings(item);
            setState(btn, submenu, !isOpen);
        });

        // Esc closes this submenu and returns focus to its toggle button
        btn.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                setState(btn, submenu, false);
                btn.focus();
            }
        });
    });

    // ─── Click outside: close submenus and/or mobile nav ────────────────────
    document.addEventListener('click', (e) => {
        const inNav = nav.contains(e.target);
        const onToggle = toggle?.contains(e.target) ?? false;

        if (!inNav) closeAll();
        if (!inNav && !onToggle && isMobileNavOpen()) closeMobileNav();
    });

    // ─── Escape key ──────────────────────────────────────────────────────────
    // Per-button handlers already manage submenu-close + focus. The document
    // handler handles closing the mobile nav panel when Escape is pressed from
    // anywhere outside an open submenu (e.g. from the toggle button itself).
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (isMobileNavOpen()) {
            closeMobileNav();
            toggle.focus();
        }
    });

    // ─── Helpers ─────────────────────────────────────────────────────────────
    function setState(btn, submenu, open) {
        btn.classList.toggle(OPEN, open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        submenu.classList.toggle(OPEN, open);
        submenu.setAttribute('aria-hidden', open ? 'false' : 'true');
        submenu.inert = !open;
    }

    function closeSiblings(current) {
        current.parentElement?.querySelectorAll(`:scope > ${ITEM}`).forEach((sibling) => {
            if (sibling === current) return;
            const sibBtn = sibling.querySelector(':scope > .sub-toggle');
            const sibMenu = sibling.querySelector(':scope > .sub-menu');
            if (sibBtn && sibMenu) setState(sibBtn, sibMenu, false);
        });
    }

    function closeAll() {
        nav.querySelectorAll('.sub-toggle[aria-expanded="true"]').forEach((btn) => {
            const submenu = document.getElementById(btn.getAttribute('aria-controls'));
            if (submenu) setState(btn, submenu, false);
        });
    }
});
